import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaInfoCircle, FaStar, FaArrowLeft, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { getMyAccountSnapshot } from '../services/firestoreService';
import { db } from '../firebase';
import { isAlaCarteItemHiddenFromPurchase } from '../utils/programPricing';
import { useAuth } from '../contexts/AuthContext';
import StartSidebar from '../components/StartSidebar';
import PortalBackground from '../components/PortalBackground';

const RoadmapBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const isStartContext = location.pathname.startsWith('/start/');
  const [selectedTier, setSelectedTier] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [billingCycles, setBillingCycles] = useState({});
  const [programs, setPrograms] = useState({});
  const [availableItems, setAvailableItems] = useState([]);
  const [itemOrder, setItemOrder] = useState({});
  const [showcaseOrder, setShowcaseOrder] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [collapsedDepartments, setCollapsedDepartments] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);

 // Load data from Firestore on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Load programs data
        const programsSnapshot = await getDocs(collection(db, 'programs'));
        if (!programsSnapshot.empty) {
          const programsData = {};
          programsSnapshot.forEach(doc => {
            const programData = doc.data();
            if (programData && typeof programData === 'object') {
              if (!programData.features || typeof programData.features !== 'object') {
                programData.features = {};
              }
              if (!programData.name) programData.name = doc.id;
              if (!programData.description) programData.description = 'Program description';
              if (!programData.pricePerMonth) programData.pricePerMonth = 0;
              if (typeof programData.isMostPopular !== 'boolean') programData.isMostPopular = false;
              
              programsData[doc.id] = programData;
            }
          });
          setPrograms(programsData);
          
          // Set first program as selected if none selected
          if (!selectedTier || !programsData[selectedTier]) {
            setSelectedTier(Object.keys(programsData)[0] || '');
          }
        }

        // Load billing cycles data
        const billingSnapshot = await getDocs(collection(db, 'billingCycles'));
        if (!billingSnapshot.empty) {
          const billingData = {};
          billingSnapshot.forEach(doc => {
            billingData[doc.id] = doc.data();
          });
          setBillingCycles(billingData);
          
          // Set first billing cycle as default
          if (Object.keys(billingData).length > 0) {
            setBillingCycle(Object.keys(billingData)[0]);
          }
        }

        // Load available items from alaCarteItems collection
        const itemsSnapshot = await getDocs(collection(db, 'alaCarteItems'));
        const itemsData = itemsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAvailableItems(itemsData);

        // Load item order
        await loadItemOrder();
        
        // Load showcase order
        await loadShowcaseOrder();
        
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Load item order from Firestore
  const loadItemOrder = async () => {
    try {
      const orderDoc = await getDocs(collection(db, 'settings'));
      const orderData = orderDoc.docs.find(doc => doc.id === 'itemOrder');
      if (orderData) {
        setItemOrder(orderData.data().order || {});
      }
    } catch (error) {
      console.error('Error loading item order:', error);
    }
  };

  // Load showcase order from Firestore
  const loadShowcaseOrder = async () => {
    try {
      const orderDoc = await getDocs(collection(db, 'settings'));
      const showcaseOrderData = orderDoc.docs.find(doc => doc.id === 'showcaseOrder');
      if (showcaseOrderData) {
        setShowcaseOrder(showcaseOrderData.data().order || {});
      }
    } catch (error) {
      console.error('Error loading showcase order:', error);
    }
  };

  // Get ordered showcase features for a program
  const getOrderedShowcaseFeatures = (programKey) => {
    const program = programs[programKey];
    if (!program || !program.features) return [];
    
    const showcaseFeatures = Object.entries(program.features)
      .filter(([_, featureData]) => featureData.showcase)
      .map(([featureName, _]) => featureName);
    
    const savedOrder = showcaseOrder[programKey] || [];
    
    if (savedOrder.length === 0 && showcaseFeatures.length > 0) {
      return showcaseFeatures;
    }
    
    // Sort features according to saved order
    const orderedFeatures = [];
    savedOrder.forEach(featureName => {
      if (showcaseFeatures.includes(featureName)) {
        orderedFeatures.push(featureName);
      }
    });
    
    // Add any new showcase features that weren't in the saved order
    showcaseFeatures.forEach(featureName => {
      if (!orderedFeatures.includes(featureName)) {
        orderedFeatures.push(featureName);
      }
    });
    
    return orderedFeatures;
  };

  // Get ordered items for a department
  const getOrderedItems = (department) => {
    // Unpromoted items aren't for sale, so they stay out of the comparison table.
    const deptItems = availableItems.filter(
      item => item.department === department && !isAlaCarteItemHiddenFromPurchase(item)
    );
    const savedOrder = itemOrder[department] || [];
    
    if (savedOrder.length === 0 && deptItems.length > 0) {
      return deptItems;
    }
    
    // Sort items according to saved order
    const orderedItems = [];
    savedOrder.forEach(orderedItem => {
      const item = deptItems.find(deptItem => deptItem.id === orderedItem.id);
      if (item) {
        orderedItems.push(item);
      }
    });
    
    // Add any new items that weren't in the saved order
    deptItems.forEach(item => {
      if (!orderedItems.find(orderedItem => orderedItem.id === item.id)) {
        orderedItems.push(item);
      }
    });
    
    return orderedItems;
  };

  // Toggle department collapse state
  const toggleDepartment = (department) => {
    setCollapsedDepartments(prev => ({
      ...prev,
      [department]: !prev[department]
    }));
  };

  // Show item details modal
  const showItemDetails = (item) => {
    setSelectedItem(item);
    setShowItemModal(true);
  };

  // Close item details modal
  const closeItemModal = () => {
    setShowItemModal(false);
    setSelectedItem(null);
  };

  // Save program selection data to user document and lead account
  const saveProgramSelection = async (programData, programKey, billingCycle) => {
    try {
      if (!currentUser) {
        console.error('No current user found');
        return;
      }

      console.log('Saving program selection for user:', currentUser.email);
      console.log('Program data:', programData);
      console.log('Program key:', programKey);
      console.log('Billing cycle:', billingCycle);

      // Capitalize billing cycle for proper display
      const capitalizedBillingCycle = billingCycle.charAt(0).toUpperCase() + billingCycle.slice(1);
      
      // Save to user's document
      const userData = {
        selectedProgram: programData.name,
        selectedProgramKey: programKey,
        billingCycle: capitalizedBillingCycle,
        programSelectionData: {
          program: programData,
          programKey: programKey,
          billingCycle: capitalizedBillingCycle,
          selectedAt: new Date().toISOString()
        },
        lastUpdated: new Date().toISOString()
      };

      await updateDoc(doc(db, 'users', currentUser.uid), userData);
      console.log('Program selection saved to user document');

      // Now try to find and update the lead account
      console.log('Attempting to save program selection to lead account for user:', currentUser.email);
      
      // Find the lead account associated with this user
      let accountsSnapshot = await getMyAccountSnapshot('Leads');
      
      if (!accountsSnapshot.empty) {
        const leadAccount = accountsSnapshot.docs[0];
        const leadAccountRef = doc(db, 'accounts', leadAccount.id);
        
        console.log('Updating lead account with program selection data');
        
        await updateDoc(leadAccountRef, {
          selectedProgram: programData.name,
          selectedProgramKey: programKey,
          billingCycle: capitalizedBillingCycle,
          programSelectionData: {
            program: programData,
            programKey: programKey,
            billingCycle: capitalizedBillingCycle,
            selectedAt: new Date().toISOString()
          },
          lastUpdated: new Date().toISOString()
        });
        
        console.log('Program selection saved to lead account');
      } else {
        console.log('No lead account found for user:', currentUser.email);
      }
      
    } catch (error) {
      console.error('Error saving program selection:', error);
    }
  };

  // Validate program data to ensure it has required structure
  const validateProgram = (program) => {
    if (!program || typeof program !== 'object') {
      return {
        name: 'Unknown Program',
        description: 'Program description',
        pricePerMonth: 0,
        isMostPopular: false,
        features: {}
      };
    }
    
    return {
      name: program.name || 'Unknown Program',
      description: program.description || 'Program description',
      pricePerMonth: program.pricePerMonth || 0,
      isMostPopular: program.isMostPopular || false,
      features: program.features && typeof program.features === 'object' ? program.features : {}
    };
  };

  const getAnnualPrice = (pricePerMonth) => {
    if (billingCycle === 'annual' && billingCycles?.annual) {
      return Math.round(pricePerMonth * billingCycles.annual.months * (1 - billingCycles.annual.discount / 100));
    } else if (billingCycle === 'quarterly' && billingCycles?.quarterly) {
      return Math.round(pricePerMonth * billingCycles.quarterly.months * (1 - billingCycles.quarterly.discount / 100));
    }
    return pricePerMonth * 12;
  };

  const getMonthlyPrice = (pricePerMonth) => {
    if (billingCycle === 'annual' && billingCycles?.annual) {
      return Math.round(pricePerMonth * (1 - billingCycles.annual.discount / 100));
    } else if (billingCycle === 'quarterly' && billingCycles?.quarterly) {
      return Math.round(pricePerMonth * (1 - billingCycles.quarterly.discount / 100));
    }
    return pricePerMonth;
  };

  const getQuarterlyPrice = (pricePerMonth) => {
    if (billingCycle === 'quarterly' && billingCycles?.quarterly) {
      return Math.round(pricePerMonth * billingCycles.quarterly.months * (1 - billingCycles.quarterly.discount / 100));
    }
    return pricePerMonth * 3;
  };

  // Get program color based on name or default
  const getProgramColor = (programName) => {
    const colors = {
      'Business Launch Blueprint': 'from-blue-500 to-blue-600',
      'Operational Excellence': 'from-green-500 to-green-600',
      'Financial Mastery': 'from-purple-500 to-purple-600',
      'Sales & Marketing Excellence': 'from-pink-500 to-pink-600',
      'Technology & Innovation': 'from-indigo-500 to-indigo-600'
    };
    return colors[programName] || 'from-red-500 to-red-600';
  };

  // Get department color for styling
  const getDepartmentColor = (department) => {
    const colors = {
      'General': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      'Sales': 'bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-200',
      'Operations': 'bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-200',
      'Finance': 'bg-purple-100 text-purple-800 dark:bg-purple-700 dark:text-purple-200',
      'Technology': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-700 dark:text-indigo-200',
      'Marketing': 'bg-pink-100 text-pink-800 dark:bg-pink-700 dark:text-pink-200',
      'HR': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-700 dark:text-yellow-200'
    };
    return colors[department] || colors['General'];
  };

  return (
    <div className="relative min-h-screen">
      <PortalBackground />
      <div className="relative">
      <StartSidebar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Row */}
        <div className="flex items-center justify-center mb-8">
          {/* Page Title */}
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
            Choose Your Ideal Growth Plan
          </h1>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Loading programs and billing cycles...
            </div>
          </div>
        )}

        {/* Content - Only show when not loading */}
        {!isLoading && (
          <>
            {/* Billing Toggle */}
            <div className="flex justify-center mb-8">
              <div className="bg-white dark:bg-gray-900 rounded-xl p-1 shadow-sm border border-gray-200 dark:border-gray-800">
                <div className="flex">
                  {['monthly', 'quarterly', 'annual'].map((cycleKey) => {
                    const cycle = billingCycles[cycleKey];
                    if (!cycle) return null;
                    
                    return (
                      <button
                        key={cycleKey}
                        onClick={() => setBillingCycle(cycleKey)}
                        className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                          billingCycle === cycleKey
                            ? 'bg-red-600 text-white'
                            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        {cycle.name}
                        {cycle.discount > 0 && (
                          <span className="ml-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                            Save {cycle.discount}%
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Program Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {programs && Object.entries(programs).map(([programKey, program], index) => {
                const validatedProgram = validateProgram(program);
                const isMiddleCard = index === Math.floor(Object.keys(programs).length / 2);
                return (
                  <div
                    key={programKey}
                    className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 transition-all duration-200 hover:shadow-xl h-full flex flex-col ${
                      isMiddleCard
                        ? 'border-red-500'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    {validatedProgram.isMostPopular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <span className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                          Most Popular
                        </span>
                      </div>
                    )}
                    
                    <div className="p-8 flex flex-col h-full">
                      <div className="text-center mb-6">
                        <h3 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">{validatedProgram.name}</h3>
                        <p className="text-gray-600 dark:text-gray-300 text-sm">{validatedProgram.description}</p>
                      </div>

                      <div className="text-center mb-6">
                        {billingCycle === 'monthly' && (
                          <div className="flex items-baseline justify-center">
                            <span className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white">$</span>
                            <span className="text-5xl font-bold text-gray-900 dark:text-white ml-1">
                              {validatedProgram.pricePerMonth}
                            </span>
                            <span className="text-gray-600 dark:text-gray-300 ml-2">/month</span>
                          </div>
                        )}
                        {billingCycle === 'quarterly' && billingCycles?.quarterly && (
                          <>
                            <div className="flex items-baseline justify-center">
                              <span className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white">$</span>
                              <span className="text-5xl font-bold text-gray-900 dark:text-white ml-1">
                                {getQuarterlyPrice(validatedProgram.pricePerMonth)}
                              </span>
                              <span className="text-gray-600 dark:text-gray-300 ml-2">/quarter</span>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              ${getMonthlyPrice(validatedProgram.pricePerMonth)}/month
                            </p>
                          </>
                        )}
                        {billingCycle === 'annual' && billingCycles?.annual && (
                          <>
                            <div className="flex items-baseline justify-center">
                              <span className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white">$</span>
                              <span className="text-5xl font-bold text-gray-900 dark:text-white ml-1">
                                {getMonthlyPrice(validatedProgram.pricePerMonth)}
                              </span>
                              <span className="text-gray-600 dark:text-gray-300 ml-2">/month</span>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              ${getAnnualPrice(validatedProgram.pricePerMonth)}/year
                            </p>
                          </>
                        )}
                      </div>

                      <button
                        onClick={async () => {
                          // Save program selection data before navigating
                          await saveProgramSelection(validatedProgram, programKey, billingCycle);
                          
                          // Handle step completion for start flow
                          if (isStartContext && currentUser) {
                            try {
                              // Get existing user data to preserve completed steps
                              const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                              let existingCompletedSteps = [];
                              
                              if (userDoc.exists()) {
                                const userData = userDoc.data();
                                if (userData.startFlowProgress && userData.startFlowProgress.completedSteps) {
                                  existingCompletedSteps = userData.startFlowProgress.completedSteps;
                                }
                              }
                              
                              // Add step 4 if not already completed
                              if (!existingCompletedSteps.includes(4)) {
                                existingCompletedSteps.push(4);
                              }
                              
                              // Update start flow progress to mark step 4 (Solutions) as completed
                              await updateDoc(doc(db, 'users', currentUser.uid), {
                                startFlowProgress: {
                                  completedSteps: existingCompletedSteps,
                                  lastUpdated: new Date().toISOString()
                                },
                                lastUpdated: new Date().toISOString()
                              });
                            } catch (error) {
                              console.error('Error saving step progress:', error);
                            }
                          }
                          
                          navigate('/start/dashboard');
                        }}
                        className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors mb-6 mt-auto ${
                          isMiddleCard
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : `bg-gradient-to-r ${getProgramColor(validatedProgram.name)} text-white hover:opacity-90`
                        }`}
                      >
                        Choose Program
                      </button>

                      {/* Showcase Features */}
                      {validatedProgram.features && Object.entries(validatedProgram.features).filter(([_, featureData]) => featureData.showcase).length > 0 && (
                        <div className="space-y-4 flex-1">
                          {getOrderedShowcaseFeatures(programKey).map((feature) => {
                            const featureData = validatedProgram.features[feature];
                            const isCheckbox = featureData.quantity === '0';
                            // Find the item in availableItems to get its icon
                            const item = availableItems.find(availItem => availItem.name === feature);
                            const itemIcon = item?.icon || '📦';
                            
                            return (
                              <div key={feature} className="flex items-center justify-between p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                <button
                                  onClick={() => {
                                    if (item) {
                                      showItemDetails(item);
                                    }
                                  }}
                                  className="flex items-center flex-1 text-left hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                >
                                  <span className="text-lg mr-2">{itemIcon}</span>
                                  <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                                </button>
                                <span className="text-sm font-medium text-gray-900 dark:text-white ml-2">
                                  {isCheckbox ? (
                                    <FaCheck className="text-green-500 text-xs" />
                                  ) : (
                                    featureData.quantity
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Items Comparison Table */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white text-center">Complete Items Comparison</h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700">
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Items</th>
                      {programs && Object.keys(programs).map(programKey => {
                        const validatedProgram = validateProgram(programs[programKey]);
                        return (
                          <th key={programKey} className="px-6 py-4 text-center text-sm font-semibold text-gray-600 dark:text-gray-300">
                            {validatedProgram.name}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {(() => {
                      const rows = [];
                      
                      // Add department sections
                      const departments = ['General', 'Sales', 'Operations', 'Finance', 'Technology', 'Marketing', 'HR'];
                      departments.forEach(dept => {
                        const deptItems = getOrderedItems(dept);
                        if (deptItems.length > 0) {
                          const isCollapsed = collapsedDepartments[dept];
                          
                          // Add department header row
                          rows.push(
                            <tr key={`dept-${dept}`} className="bg-gray-50 dark:bg-gray-700">
                              <td colSpan={programs && Object.keys(programs).length > 0 ? Object.keys(programs).length + 1 : 1} className="px-6 py-3">
                                <button
                                  onClick={() => toggleDepartment(dept)}
                                  className="flex items-center space-x-2 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                                >
                                  {isCollapsed ? (
                                    <FaChevronRight className="h-4 w-4" />
                                  ) : (
                                    <FaChevronDown className="h-4 w-4" />
                                  )}
                                  <span>{dept}</span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">({deptItems.length} items)</span>
                                </button>
                              </td>
                            </tr>
                          );
                          
                          // Add department items (only if not collapsed)
                          if (!isCollapsed) {
                            deptItems.forEach((item) => {
                              rows.push(
                                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                  <td className="px-6 py-4">
                                    <div className="flex items-center space-x-3">
                                      <button
                                        onClick={() => showItemDetails(item)}
                                        className="flex items-center space-x-3 flex-1 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 p-2 rounded-md transition-colors"
                                      >
                                        <div className="text-2xl">{item.icon || '📦'}</div>
                                        <div className="flex items-center space-x-2">
                                          <div className="font-medium text-gray-900 dark:text-white text-sm">{item.name}</div>
                                          <FaInfoCircle className="h-4 w-4 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" />
                                        </div>
                                      </button>
                                    </div>
                                  </td>
                                  {programs && Object.entries(programs).map(([programKey, program]) => {
                                    const validatedProgram = validateProgram(program);
                                    const currentQuantity = validatedProgram.features[item.name]?.quantity || '0';
                                    const isCheckbox = currentQuantity === '0';
                                    const isIncluded = validatedProgram.features[item.name] !== undefined;
                                    
                                    return (
                                      <td key={programKey} className="px-6 py-4 text-center">
                                        {isIncluded ? (
                                          <div className="flex items-center justify-center">
                                            {isCheckbox ? (
                                              <FaCheck className="text-green-500 text-sm" />
                                            ) : (
                                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                {currentQuantity}
                                              </span>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="flex items-center justify-center">
                                            <FaTimes className="text-red-500 text-sm" />
                                          </div>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            });
                          }
                        }
                      });
                      
                      return rows;
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

          </>
        )}
      </div>

      {/* Item Details Modal */}
      {showItemModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-md">
            <div className="bg-red-600 rounded-t-lg p-4">
              <div className="flex justify-center items-center">
                <h3 className="text-lg font-semibold text-white">Item Details</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="text-4xl">{selectedItem.icon || '📦'}</div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 dark:text-white">{selectedItem.name}</h4>
                  <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${getDepartmentColor(selectedItem.department)}`}>
                    {selectedItem.department}
                  </div>
                </div>
              </div>
              <div className="mb-6">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</h5>
                <p className="text-gray-600 dark:text-gray-400">{selectedItem.description}</p>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={closeItemModal}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default RoadmapBuilder; 